// 「最近刪除」的 cue：Cue 庫頁、編輯頁 Cue 庫分頁各有一個入口（沒有刪掉的 cue 就不顯示）+ 視窗。
// 刪掉的 cue 留 30 天，這段時間導演、專案製作、全站製作可以救回來（畫面、備註、順序、起始都在）；30 天後排程才真的清掉。
// 樣子跟「最近刪除的歌」（DeletedSongs.tsx）同一套。後台：cf-worker/cueLibraryTrash.mjs。
import { useCallback, useEffect, useRef, useState } from 'react';
import { History, LoaderCircle, RotateCcw, X } from 'lucide-react';
import Icon from './Icon';
import { listDeletedLibraryCues, restoreLibraryCue, type DeletedLibraryCue } from '../lib/cueLibraryApi';
import {
  LIBRARY_CUE_KEEP_DAYS, cueTrashEntryLabel, daysLeftText, deletedCueInside, deletedCueMeta, isSoon, restoreCueErrorText, restoredCueToast,
} from '../lib/libraryCueTrash';
import { toast } from '../lib/toast';
import { useEscapeClose } from '../lib/useEscapeClose';
import './SongVideosDialog.css';
import './DeletedVersions.css';
import './DeletedSongs.css';

interface EntryProps {
  projectId: string;
  /** 導演、專案製作、全站製作才看得到；別人不讀、不顯示 */
  enabled: boolean;
  /** 變了就重新讀（例如剛刪了一個 cue） */
  reloadKey?: unknown;
  /** 救回來了 → Cue 清單重新讀 */
  onRestored: () => void | Promise<unknown>;
  /** 入口按鈕的 class（不給 = 一般的小按鈕） */
  className?: string;
}

export default function DeletedLibraryCuesEntry({ projectId, enabled, reloadKey, onRestored, className }: EntryProps) {
  const [items, setItems] = useState<DeletedLibraryCue[]>([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    if (!enabled) { setItems([]); return; }
    try {
      const r = await listDeletedLibraryCues(projectId);
      setItems(r.libraryCues);
    } catch {
      // 讀不到（沒權限、網路不穩、舊版後台沒有這個功能）→ 入口先不顯示，下次刪 cue 或重新整理再讀
      setItems([]);
    }
  }, [projectId, enabled]);
  useEffect(() => { void load(); }, [load, reloadKey]);
  // 最後一個也救回來了 → 關掉視窗
  useEffect(() => { if (open && items.length === 0) setOpen(false); }, [open, items.length]);

  if (!enabled || items.length === 0) return null;
  return (
    <>
      <button type="button" className={className ?? 'btn btn--ghost btn--sm'} onClick={() => { setOpen(true); void load(); }}
        title={`刪掉的 cue 留 ${LIBRARY_CUE_KEEP_DAYS} 天，這段時間可以救回來`}>
        <Icon icon={History} size="sm" /> {cueTrashEntryLabel(items.length)}
      </button>
      <DeletedLibraryCuesDialog open={open} onClose={() => setOpen(false)} items={items} projectId={projectId} reload={load} onRestored={onRestored} />
    </>
  );
}

interface DialogProps {
  open: boolean;
  onClose: () => void;
  items: DeletedLibraryCue[];
  projectId: string;
  reload: () => Promise<void>;
  onRestored: () => void | Promise<unknown>;
}

export function DeletedLibraryCuesDialog({ open, onClose, items, projectId, reload, onRestored }: DialogProps) {
  useEscapeClose(open, onClose);
  const panelRef = useRef<HTMLDivElement>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  useEffect(() => { if (open) panelRef.current?.focus(); }, [open]);

  async function restore(c: DeletedLibraryCue) {
    setBusyId(c.id);
    try {
      const r = await restoreLibraryCue(projectId, c.id);
      toast.success(restoredCueToast(r));
      await reload();
      await onRestored();
    } catch (e) {
      toast.error(restoreCueErrorText(e));
      await reload();   // 剛好被別人救回來 / 超過期限了 → 清單跟著更新
    } finally {
      setBusyId(null);
    }
  }

  if (!open) return null;
  return (
    <div className="songvid-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={panelRef} className="songvid delvid-dialog delsong-dialog" role="dialog" aria-modal="true" aria-labelledby="delcue-title" tabIndex={-1}
        onKeyDown={(e) => e.stopPropagation()}>
        <header className="songvid__head">
          <div className="songvid__titles">
            <h2 id="delcue-title"><Icon icon={History} /> 最近刪除的 cue</h2>
            <p className="songvid__sub">刪掉的 cue 留 {LIBRARY_CUE_KEEP_DAYS} 天，可以救回來；{LIBRARY_CUE_KEEP_DAYS} 天後就清掉了。</p>
          </div>
          <button type="button" className="songvid__close" onClick={onClose} title="關閉" aria-label="關閉">
            <Icon icon={X} size="md" />
          </button>
        </header>
        <div className="songvid__body">
          <ul className="delvid__list" aria-label="最近刪除的 cue">
            {items.map(c => {
              const inside = deletedCueInside(c);
              return (
                <li key={c.id} className="delvid-row delsong-row">
                  <div className="delvid-row__main">
                    <div className="delvid-row__title" title={`${c.label} ${c.name}`}>
                      <span className="delsong-row__code">{c.label}</span>
                      {c.name}
                    </div>
                    <div className="delvid-row__meta">
                      {deletedCueMeta(c)}
                      {' · '}
                      <span className={isSoon(c.daysLeft) ? 'delvid-row__left is-soon' : 'delvid-row__left'}>{daysLeftText(c.daysLeft)}</span>
                    </div>
                    {(inside || c.note) && <div className="delvid-row__meta">{[inside, c.note].filter(Boolean).join(' · ')}</div>}
                  </div>
                  <button type="button" className="songvid-btn" onClick={() => void restore(c)} disabled={busyId !== null}
                    aria-label={`救回來：${c.label} ${c.name}`}>
                    {busyId === c.id ? <><Icon icon={LoaderCircle} spin size="sm" /> 救回中…</> : <><Icon icon={RotateCcw} size="sm" /> 救回來</>}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
        <footer className="songvid__foot">
          <span className="songvid__count">{items.length} 個</span>
          <button type="button" className="btn btn--ghost" onClick={onClose}>關閉</button>
        </footer>
      </div>
    </div>
  );
}
